import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Newspaper, Calendar as Cal, Users, Eye, ArrowRight, MapPin, CheckCircle2, Circle, Sparkles, Trophy, Award } from "lucide-react";
import CompanyLogo from "@/components/CompanyLogo";
import { useLang } from "@/context/LangContext";

export default function MemberDashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const [stats, setStats] = useState({ news_published: 0, events_joined: 0, meetings: 0, profile_views: 0, company: null, onboarding_steps: {}, onboarding_score: 0, onboarding_done: false });
  const [events, setEvents] = useState([]);
  const [meetings, setMeetings] = useState({ as_visitor: [], as_exhibitor: [] });

  const [statsLoaded, setStatsLoaded] = useState(false);

  useEffect(() => {
    api.get("/my/stats").then((r) => { setStats(r.data); setStatsLoaded(true); }).catch(() => setStatsLoaded(true));
    api.get("/events?when=upcoming").then((r) => setEvents(r.data.slice(0, 3))).catch(() => {});
    api.get("/my/meetings").then((r) => setMeetings(r.data)).catch(() => {});
  }, []);

  // Sprint 89b — Backend now computes onboarding_score directly. Falls back to
  // client-side computation for older cached responses (statsLoaded guard).
  const profileComplete = stats.onboarding_steps?.profile_complete ?? false;
  const hasPublished = stats.onboarding_steps?.news_published ?? false;
  const hasConnected = stats.onboarding_steps?.connection_made ?? false;
  const doneCount = [profileComplete, hasPublished, hasConnected].filter(Boolean).length;
  const score = stats.onboarding_score ?? Math.round(doneCount * 100 / 3);
  const allDone = doneCount === 3;

  // Snapshot "now" at first render — used by the J-X countdown chips below.
  const nowMs = useMemo(() => Date.now(), []);

  return (
    <PortalLayout sidebar="member">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-navy md:text-4xl">Bonjour {user?.first_name} 👋</h1>
        <p className="mt-1 text-neutral-600">{t("portal_memberdashboard_01")}</p>
      </div>

      {/* Sprint 89b — Onboarding widget with progress bar + gamification badges.
          Visible in all states: incomplete → progress + CTAs; complete → celebration. */}
      {statsLoaded && (
        <div className={`mb-8 rounded-2xl border-2 p-6 ${allDone ? "border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-amber-50/40" : "border-wine-200 bg-gradient-to-br from-wine-50 via-white to-amber-50/40"}`} data-testid="onboarding-widget">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {allDone ? <Trophy className="h-5 w-5 text-amber-500" /> : <Sparkles className="h-4 w-4 text-wine-500" />}
              <h2 className="text-base font-bold text-navy">
                {allDone ? "🎉 Onboarding complet — Bienvenue dans la communauté !" : "3 actions clés pour bien démarrer"}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-neutral-600" data-testid="onboarding-score-label">{doneCount}/3 · {score}%</span>
              <div className="h-2 w-32 rounded-full bg-neutral-200 overflow-hidden">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${allDone ? "bg-emerald-500" : "bg-wine-500"}`}
                  style={{ width: `${score}%` }}
                  data-testid="onboarding-progress-bar"
                />
              </div>
            </div>
          </div>
          <p className="mt-1 text-xs text-neutral-600">
            {allDone
              ? "Tu as débloqué tous les badges Wine Founder. Continue à publier, participer et connecter pour rester rayonnant dans la communauté 🍷"
              : "Complétez ces étapes pour tirer pleinement parti de la communauté et gagner vos badges Wine Founder."}
          </p>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <ChecklistCard done={profileComplete} icon={Users} title="Profil entreprise complet"
              badge="🏆 Vitrine" desc="Logo, description (≥30 mots), tags : votre vitrine au sein de la communauté."
              cta="Compléter mon profil" link="/portal/profile" testid="onb-action-profile" />
            <ChecklistCard done={hasPublished} icon={Newspaper} title="Publiez votre 1ère actualité"
              badge="📣 Rayonnement" desc="Levée de fonds, sortie produit, partenariat… faites-le savoir."
              cta="Publier une actualité" link="/portal/news" testid="onb-action-news" />
            <ChecklistCard done={hasConnected} icon={Users} title="Faites une 1ère mise en relation"
              badge="🤝 Networker" desc="Réservez un RDV via matchmaking ou lors d'un événement."
              cta="Explorer les membres" link="/portal/matchmaking" testid="onb-action-connect" />
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPI icon={Newspaper} label={t("portal_memberdashboard_90")} value={stats.news_published} sub="Publiez plus →" />
        <KPI icon={Cal} label={t("portal_memberdashboard_91")} value={stats.events_joined} />
        <KPI icon={Users} label="Rendez-vous" value={(meetings.as_visitor?.length || 0) + (meetings.as_exhibitor?.length || 0)} />
        <KPI icon={Eye} label={t("portal_memberdashboard_92")} value={stats.profile_views} sub="+18 ce mois-ci" />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl border border-neutral-100 bg-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl text-neutral-900">{t("portal_memberdashboard_02")}</h2>
            <Link to="/portal/profile" className="text-xs font-semibold text-wine-600 hover:text-wine-700" data-testid="dashboard-edit-profile">{t("portal_memberdashboard_93")}</Link>
          </div>
          {stats.company ? (
            <div className="mt-4 flex gap-4">
              <CompanyLogo src={stats.company.logo} name={stats.company.name} size={80} className="rounded-xl" />
              <div className="flex-1">
                <h3 className="font-serif text-lg text-neutral-900">{stats.company.name}</h3>
                <p className="mt-1 line-clamp-2 text-sm text-neutral-600">{stats.company.description}</p>
                <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                  <span className="rounded-full bg-wine-50 px-2 py-0.5 font-semibold text-wine-700">{stats.company.category}</span>
                  <span className="inline-flex items-center gap-1 text-neutral-500"><MapPin className="h-3 w-3" />{stats.company.city}, {stats.company.country}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4 rounded-xl bg-wine-50/50 p-4 text-sm text-neutral-600">{t("portal_memberdashboard_03")}<Link to="/portal/profile" className="font-semibold text-wine-700">{t("portal_memberdashboard_04")}</Link>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-neutral-100 bg-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl text-neutral-900">{t("portal_memberdashboard_05")}</h2>
            <Link to="/portal/events" className="text-xs font-semibold text-wine-600" data-testid="dashboard-see-events">{t("portal_memberdashboard_06")}</Link>
          </div>
          <div className="mt-4 space-y-3">
            {events.map((e) => {
              // J-X countdown chip when event is within 30 days
              const ms = e.date ? new Date(e.date).getTime() - nowMs : -1;
              const days = ms >= 0 ? Math.ceil(ms / (24 * 60 * 60 * 1000)) : -1;
              const showCountdown = days >= 0 && days <= 30;
              const cls = days <= 7 ? "bg-rose-500" : days <= 14 ? "bg-amber-500" : "bg-wine-600";
              return (
                <Link key={e.id} to={`/events/${e.slug}`} className="block rounded-xl border border-neutral-100 p-3 hover:bg-wine-50/40" data-testid={`dashboard-event-${e.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold uppercase text-wine-600">{new Date(e.date).toLocaleDateString("fr", { day: "numeric", month: "short" })}</div>
                    {showCountdown && (
                      <span className={`shrink-0 rounded-full ${cls} px-2 py-0.5 text-[9px] font-bold uppercase text-white`} data-testid={`dashboard-event-countdown-${e.id}`}>
                        ⏳ {days === 0 ? "Jour J" : `J-${days}`}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 font-serif text-sm text-neutral-900">{e.title}</div>
                  <div className="mt-1 text-xs text-neutral-500">{e.location}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-8 rounded-3xl border border-wine-100 bg-gradient-to-br from-wine-50 to-white p-6 md:p-8">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h3 className="font-serif text-2xl text-neutral-900">{t("portal_memberdashboard_07")}</h3>
            <p className="mt-1.5 text-neutral-600">{t("portal_memberdashboard_08")}</p>
          </div>
          <Link to="/portal/news" className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-6 py-3 text-sm font-semibold text-white hover:bg-wine-600" data-testid="dashboard-publish-cta">{t("portal_memberdashboard_09")}<ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </PortalLayout>
  );
}

function KPI({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-neutral-100 bg-white p-5">
      <div className="grid h-10 w-10 place-items-center rounded-xl bg-wine-50 text-wine-600"><Icon className="h-5 w-5" /></div>
      <div className="mt-3 font-serif text-3xl font-semibold text-neutral-900">{value}</div>
      <div className="mt-0.5 text-xs font-medium text-neutral-600">{label}</div>
      {sub && <div className="mt-1 text-xs text-wine-600">{sub}</div>}
    </div>
  );
}

function ChecklistCard({ done, icon: Icon, title, desc, cta, link, testid, badge }) {
  return (
    <div className={`rounded-xl border p-4 transition-all ${done ? "border-emerald-300 bg-emerald-50/50" : "border-wine-200 bg-white hover:shadow-md"}`} data-testid={testid}>
      <div className="flex items-start gap-2">
        <div className={`grid h-7 w-7 place-items-center rounded-full shrink-0 ${done ? "bg-emerald-500 text-white" : "bg-wine-100 text-wine-600"}`}>
          {done ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1.5">
            <Icon className="h-3.5 w-3.5 text-neutral-400" />
            <div className="text-sm font-bold text-navy">{title}</div>
          </div>
          <p className="mt-1 text-[11px] text-neutral-600 leading-snug">{desc}</p>
          {badge && (
            <div className={`mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${done ? "bg-amber-100 text-amber-700" : "bg-neutral-100 text-neutral-400"}`} data-testid={`${testid}-badge`}>
              {done && <Award className="h-2.5 w-2.5" />}
              {badge}{done ? " · Débloqué" : ""}
            </div>
          )}
          {!done && (
            <Link to={link} className="mt-2 ml-2 inline-flex items-center gap-1 text-[11px] font-bold text-wine-600 hover:text-wine-700">
              {cta} <ArrowRight className="h-3 w-3" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

